// START IMPORTS
// Context imports
import {
  AppConfig,
  AppConstants,
  useSubscriptionAccessConfigContext,
} from '@ltvco/refresh-lib/ctx';
// Payments imports
import { ReactivatePausedAccountCta } from '@ltvco/refresh-lib/payments';
// Theme and UI imports
import {
  Text,
  Grid,
  Link,
  Stack,
  styled,
  CustomIcons,
  IconButton,
  Box,
} from '@ltvco/refresh-lib/theme';
// Utility imports
import {
  capitalizeEachWord,
  DateUtil,
  useScreenSize,
  useFirstRecordSearchRedirect,
} from '@ltvco/refresh-lib/utils';
// Core lib v1 imports
import {
  RightRailCard,
  useSnackbar,
  closeSnackbar,
  MobileAppsCtaCard,
  useAccount,
  EmailVerificationErrorModal,
  EmailVerificationSuccessModal,
  EmailVerificationInvalidModal,
  NeedAssistanceSidebar,
  usePremiumDataModal,
} from '@ltvco/refresh-lib/v1';
// Core lib v2 imports
import {
  EmailSettingsCard,
  MonitoredReportsModal,
  SideBarMigrationCta,
  FeatureBlockedModal,
  useFeatureBlockedModal,
} from '@ltvco/refresh-lib/v2';
// Vendor imports
import { Campaign } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { useContext, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQueryParam, StringParam } from 'use-query-params';

// App-specific imports
import { constants } from 'appConstants';

import { Sidenav } from '../components/sidenav/Sidenav';
import { SidenavContainer } from '../components/sidenav/SidenavContainer';
// END IMPORTS

// START STYLED_COMPONENTS
const CreditCardIcon = styled(CustomIcons.CreditCardIcon)(({ theme }) => ({
  fontSize: '3.75rem',
  padding: theme.spacing(0.9375),
}));

const StyledContent = styled(Box)(({ theme }) => ({
  width: '100%',
  margin: 'auto',
  minHeight: '80vh',
  backgroundColor: theme.palette.background.paper,

  [theme.breakpoints.up('md')]: {
    margin: 'auto',
    maxWidth: '1296px',
    padding: theme.spacing(3.5),
  },
}));

const StyledContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  marginBottom: theme.spacing(2),
  width: '100%',
  '&&': {
    '& > *': {
      width: '100%',
      maxWidth: '100%',
      border: `1px solid ${theme.palette.background.light}`,
      boxShadow: 'none',
      '& > button': {
        width: '95%',
      },
      '& > div button': {
        paddingRight: 0,
      },
    },
  },
}));
// END STYLED_COMPONENTS

// START CONSTANTS
const VERIFICATION_EVENT_ACTION = 'view';
const VERIFICATION_EVENT_LABEL = 'email verification result modal';
// END CONSTANTS

export function DashboardParent({
  contactUsHandler,
  handleLogout,
}: {
  contactUsHandler: () => void;
  handleLogout: () => void;
}) {
  // START HOOKS_AND_QUERIES
  const { enqueueSnackbar } = useSnackbar();
  const { routingUtils, trackEvent } = useContext(AppConfig);
  const navigate = useNavigate();
  const location = useLocation();
  const { data: account } = useAccount(true);
  useFirstRecordSearchRedirect({
    account,
    routingUtils,
    navigate,
  });
  const [statusParam, setStatusParam] = useQueryParam('status', StringParam);
  const {
    links: { propertyAdvertiseLink },
  } = useContext(AppConstants);
  const { isMobile } = useScreenSize();
  const { singleReportVariation } = useSubscriptionAccessConfigContext();
  const { triggerModal } = usePremiumDataModal();
  const {
    isFeatureBlockedModalOpen,
    closeFeatureBlockedModal,
    openFeatureBlockedModal,
  } = useFeatureBlockedModal();
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [searchType, setSearchType] = useState('');
  const [isInSingleReportVariation, setIsInSingleReportVariation] =
    useState(false);
  const [paymentShowModalNumber, setPaymentShowModalNumber] =
    useState<number>(0);
  const [monitoredReportsOpen, setMonitoredReportsOpen] = useState(false);
  const [showEmailExpiredModal, setShowEmailExpiredModal] = useState(false);
  const [showEmailSuccessModal, setShowEmailSuccessModal] = useState(false);
  const [showEmailInvalidModal, setShowEmailInvalidModal] = useState(false);
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const dateUtil = new DateUtil();
  const currentDate = new Date();
  // END DERIVED_DATA_AND_UTILS

  // START EFFECTS
  useEffect(() => {
    if (localStorage.getItem('pausedAccountSnackbarId')) {
      try {
        closeSnackbar(
          JSON.parse(localStorage.getItem('pausedAccountSnackbarId') || '')
        );
      } catch (e) {
        console.log('error', e);
      }
    }

    handleSiteNotifications();
    handleReactivationForPausedAccountsBanner();
  }, [account]);

  useEffect(() => {
    if (!account) {
      return;
    }

    const isEmailVerified = account?.account.user_info?.verified_email;
    const hasShownSuccessModal =
      account?.account.user_settings?.hide_email_verification_success;

    if (
      !isEmailVerified &&
      statusParam === 'success' &&
      !hasShownSuccessModal
    ) {
      setStatusParam(undefined, 'replaceIn');
      setShowEmailSuccessModal(true);
      trackEvent(
        'email verification success',
        VERIFICATION_EVENT_ACTION,
        VERIFICATION_EVENT_LABEL
      );
    } else if (!isEmailVerified && statusParam === 'expired') {
      setStatusParam(undefined, 'replaceIn');
      setShowEmailExpiredModal(true);
      trackEvent(
        'email verification expired',
        VERIFICATION_EVENT_ACTION,
        VERIFICATION_EVENT_LABEL
      );
    } else if (!isEmailVerified && statusParam === 'invalid') {
      setStatusParam(undefined, 'replaceIn');
      setShowEmailInvalidModal(true);
      trackEvent(
        'email verification invalid',
        VERIFICATION_EVENT_ACTION,
        VERIFICATION_EVENT_LABEL
      );
    } else if (!isEmailVerified) {
      // setShowEmailPromptModal(true);
    }
  }, [account, statusParam, setStatusParam]);

  useEffect(() => {
    if (!account) {
      return;
    }
    setIsInSingleReportVariation(singleReportVariation);
  }, [account]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isFallFromCTAEmail = urlParams.get('premiumData');

    if (isInSingleReportVariation && isFallFromCTAEmail === 'true') {
      triggerModal(true, isInSingleReportVariation);
    }
  }, [isInSingleReportVariation]);
  // END EFFECTS

  // START EVENT_HANDLERS
  const featureAction = (feature: string) => {
    setSearchType(capitalizeEachWord(feature));
    openFeatureBlockedModal();
  };

  function closeMonitoredReportsModal() {
    setMonitoredReportsOpen(false);
  }

  function showMobileAppCtas() {
    return [
      '/dashboard/reports',
      '/dashboard/lists',
      '/dashboard/monitoring',
      '/dashboard/billing',
    ].includes(location.pathname);
  }

  function handleReactivationForPausedAccountsBanner() {
    if (!account) {
      return;
    }

    const isInPausedStatus = account?.meta?.subscription_state === 'paused';
    if (!isInPausedStatus) {
      return;
    }

    const today = currentDate.toISOString();
    const expirationDate = account?.account.subscription_info?.normalize_date;

    if (!expirationDate) {
      return;
    }

    const isAfterCurrentDate = dateUtil.isAfter(today, expirationDate);

    function handleReactivationClick() {
      closeSnackbar(pausedAccountSnackbarId);
      navigate('/reactivation');
    }

    const expiredMessage = (
      <Text>
        <b>
          Your account expired on{' '}
          {dateUtil.parseDateFromString(
            account?.account.subscription_info?.normalize_date,
            'M/d/y'
          )}{' '}
          . Reactivate to regain access.
        </b>
      </Text>
    );

    const cancelledMessage = (
      <Text>
        <b>
          Your account with member ID {account?.account.user_info.user_code} has
          been cancelled.
        </b>{' '}
        <br /> You have until{' '}
        {dateUtil.parseDateFromString(
          account?.account.subscription_info?.normalize_date,
          'M/d/y'
        )}{' '}
        to continue running reports. Please reactivate now to maintain access.
        Visit{' '}
        <Link onClick={handleReactivationClick} style={{ cursor: 'pointer' }}>
          here
        </Link>{' '}
        to reactivate.
      </Text>
    );

    const message = isAfterCurrentDate ? expiredMessage : cancelledMessage;

    const pausedAccountSnackbarId = enqueueSnackbar(
      <Stack direction="row" justifyContent="space-between">
        {message}
        {getCloseButton(() => {
          closeSnackbar(pausedAccountSnackbarId);
        })}
      </Stack>,
      {
        variant: 'warning',
        persist: true,
        action: <></>,
      }
    );

    localStorage.setItem(
      'pausedAccountSnackbarId',
      JSON.stringify(pausedAccountSnackbarId)
    );
  }

  function getCloseButton(onClick: () => void) {
    return (
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={onClick}
        sx={{ marginLeft: 1 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    );
  }

  function handleSiteNotifications() {
    if (!account) {
      return;
    }
    const siteNotification = account?.meta?.notification;
    if (!siteNotification) {
      return;
    }
    const siteNotificationSnackbarId = enqueueSnackbar(
      <Text>
        <b>{siteNotification.title}</b> <br /> {siteNotification.message}
      </Text>,
      {
        variant: 'info',
        persist: true,
        action: (
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => {
              closeSnackbar(siteNotificationSnackbarId);
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        ),
      }
    );
  }
  // END EVENT_HANDLERS

  // START DATA_PREPARATION
  const neighborhoodReportLocation = location.pathname.includes(
    '/dashboard/neighborhood-report'
  );
  const isDarkWebPage = location.pathname.includes('/dashboard/dark-web');
  const isDashboard = location.pathname === '/dashboard';
  const isRewards = location.pathname.includes('/dashboard/rewards');
  const isImportList = location.pathname.includes('/dashboard/import-list');
  const isLists = location.pathname.includes('/dashboard/lists');
  const isIdMonitor = location.pathname.includes('/dashboard/id-monitor');
  const isFraud = location.pathname.includes('/dashboard/fraud');
  const isMonitoringPage = location.pathname.includes('/dashboard/monitoring');
  const isBillingPage = location.pathname.includes('/dashboard/billing');
  const isReportsPage = location.pathname.includes('/dashboard/reports');

  const showRightRail =
    !isDashboard &&
    !isRewards &&
    !neighborhoodReportLocation &&
    !isImportList &&
    !isLists &&
    !isIdMonitor &&
    !isDarkWebPage &&
    !isFraud;
  // END DATA_PREPARATION

  const RightRailComponent = (
    <Grid
      container
      size={{ xs: 12, md: 3, lg: 3 }}
      spacing={1}
      direction="column"
      marginTop={2}
      maxWidth={{ xs: '100%', md: '275px' }}
    >
      {isBillingPage && (
        <Grid
          width={'100%'}
          order={{ xs: 6, md: 1 }}
          className="needs-assistance"
        >
          <NeedAssistanceSidebar
            logout={handleLogout}
            supportImgUrl={constants.images.img24hr}
            contactUsHandler={contactUsHandler}
          />
        </Grid>
      )}
      {isMonitoringPage && (
        <Grid width={'100%'} order={2}>
          <EmailSettingsCard
            currentInstantAlertsSetting={
              account?.account?.alert_me.settings.instant_enabled || false
            }
            currentDigestAlertsSetting={
              account?.account?.alert_me.settings.digest_enabled || false
            }
            handleMonitoredReports={() => setMonitoredReportsOpen(true)}
          />
          <MonitoredReportsModal
            isOpen={monitoredReportsOpen}
            onClose={() => closeMonitoredReportsModal()}
          />
        </Grid>
      )}
      <>
        <Grid width={'100%'} order={4}>
          <RightRailCard
            icon={<CreditCardIcon />}
            title="Dark Web Scan"
            subtitle="Millions of online accounts are compromised each year"
            body="Search using your email address to see if you may have been compromised on the dark web"
            buttonText="Scan Now"
            tagText="New"
            showTag={true}
            tagOnLeft={false}
            onButtonClick={() =>
              singleReportVariation
                ? featureAction('dark web')
                : navigate('/dashboard/dark-web')
            }
          />
        </Grid>
        {isReportsPage && isMobile && (
          <Grid width={'100%'} order={2} key="mobile-migration-card">
            <StyledContainer>
              <SideBarMigrationCta />
            </StyledContainer>
          </Grid>
        )}
      </>
      {showMobileAppCtas() && (
        <Grid width={'100%'} order={5}>
          <MobileAppsCtaCard />
        </Grid>
      )}
      {account?.meta?.subscription_state === 'paused' && (
        <Grid order={5}>
          <ReactivatePausedAccountCta />
        </Grid>
      )}
      {propertyAdvertiseLink && (
        <Grid order={6} marginY={2}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Campaign />
            <Text lineHeight={1}>Advertise with us.</Text>
            <Link
              href={propertyAdvertiseLink}
              target="_blank"
              marginLeft={0.5}
              color="text.primary"
              lineHeight={1}
              fontFamily={'Nunito Sans'}
            >
              Learn more
            </Link>
          </Stack>
        </Grid>
      )}
      <FeatureBlockedModal
        isOpen={isFeatureBlockedModalOpen}
        onClose={closeFeatureBlockedModal}
        searchType={searchType}
        paymentShowModalNumber={paymentShowModalNumber}
        setPaymentShowModalNumber={setPaymentShowModalNumber}
      />
    </Grid>
  );

  const RightRail = showRightRail ? RightRailComponent : <></>;

  // START RENDER
  if (isDarkWebPage) {
    return (
      <StyledContent>
        <Sidenav onReport />
        <Grid container>
          <Grid size={12} maxWidth="100%">
            <Outlet />
          </Grid>
        </Grid>
      </StyledContent>
    );
  }

  return (
    <SidenavContainer>
      <EmailVerificationErrorModal
        open={showEmailExpiredModal}
        onClose={() => setShowEmailExpiredModal(false)}
      />
      <EmailVerificationSuccessModal
        open={showEmailSuccessModal}
        onClose={() => setShowEmailSuccessModal(false)}
      />
      <EmailVerificationInvalidModal
        open={showEmailInvalidModal}
        onClose={() => setShowEmailInvalidModal(false)}
      />
      <Grid container justifyContent={'space-between'}>
        <Grid
          size={{
            xs: 12,
            md: showRightRail ? 8 : 12,
            lg: showRightRail ? 8 : 12,
          }}
          maxWidth={{
            xs: '100%',
            md: showRightRail ? '911px !important' : '100%',
          }}
        >
          <Outlet />
        </Grid>
        {RightRail}
      </Grid>
    </SidenavContainer>
  );
  // END RENDER
}
